import { collection, deleteDoc, doc, getDocs } from "firebase/firestore";
import React, { useEffect, useState } from 'react';
import { HiChevronDoubleLeft, HiChevronDoubleRight, HiChevronLeft, HiChevronRight } from "react-icons/hi";
import { useNavigate } from 'react-router-dom';
import 'react-toastify/dist/ReactToastify.css';
import Swal from 'sweetalert2';
import LoadingSpinner from '../assets/LoadingSpinner';
import '../css/SuppliersHistory.css';
import { db } from "../firebase-config";

const SuppliersHistory = () => {
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [suppliers, setSuppliers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

       // Pagination state
       const [currentPage, setCurrentPage] = useState(1);
       const itemsPerPage = 20; // Adjust this value for more/less items per page

  const handleSearchChange = (e) => {
    setSearch(e.target.value);
  };

  const handleDateFromChange = (e) => {
    setDateFrom(e.target.value);
  };

  const handleDateToChange = (e) => {
    setDateTo(e.target.value);
  };

  const handleViewClick = (id) => {
    console.log('Viewing supplier receipt with ID:', id);
    navigate(`/pages/ViewSupplierReceipt/${id}`);
  };

  const showInfo = async () => {
    const information = await Swal.fire({
      text: "Logs expire and will be automatically removed after 14 months of storage",
      icon: 'info',
    });
  }

  useEffect(() => {
    const getSuppliers = async () => {
      try {
        // Fetch data from the 'supplierReceipts' collection
        const querySnapshot = await getDocs(collection(db, 'supplierReceipts'));
        const suppliersArray = querySnapshot.docs.map(doc => {
          const data = doc.data();
          const parsedDate = data.date instanceof Date ? data.date : new Date(data.date);
          return {
            id: doc.id,
            ...data,
            date: parsedDate
          };
        });

        const currentDate = new Date();

        // Calculate one year ago
        const oneYearAgo = new Date(currentDate.getFullYear(), currentDate.getMonth(), currentDate.getDate() - 425);
  
        // Filter out suppliers older than 1 year
        const filteredSuppliers = suppliersArray.filter(supplier => {
          return supplier.date >= oneYearAgo;
        });
  
        // Delete old suppliers from Firestore
        const oldSuppliersToDelete = suppliersArray.filter(supplier => supplier.date < oneYearAgo);
  
        // Update state with filtered suppliers
        setSuppliers(filteredSuppliers);
  
        // Delete old suppliers
        oldSuppliersToDelete.forEach(async (oldSupplier) => {
          try {
            await deleteDoc(doc(db, 'supplierReceipts', oldSupplier.id));
            console.log(`Deleted supplier ${oldSupplier.id} older than 1 year`);
          } catch (error) {
            console.error(`Error deleting old supplier ${oldSupplier.id}:`, error);
          }
        });

        suppliersArray.sort((a, b) => b.date - a.date);

        setSuppliers(suppliersArray);
        setLoading(false);
      } catch (err) {
        setError('Failed to fetch supplier receipts. Please try again later.');
        console.error('Error fetching supplier receipts:', err);
        setLoading(false);
      }
    };
    getSuppliers();
  }, []);



  // Filter suppliers based on search and date range
  const filteredSuppliers = suppliers.filter(supplier => {
    const matchesSearchName = supplier.supplierName.toLowerCase().includes(search.toLowerCase());
    const matchesSearchCode = supplier.supplierCode.toLowerCase().includes(search.toLowerCase());
    const matchesDateRange = (
      (!dateFrom || new Date(supplier.date) >= new Date(dateFrom)) &&
      (!dateTo || new Date(supplier.date) <= new Date(dateTo))
    );
    return matchesSearchName || matchesSearchCode && matchesDateRange;
  });

     // Calculate paginated data
     const indexOfLastItem = currentPage * itemsPerPage;
     const indexOfFirstItem = indexOfLastItem - itemsPerPage;
     const currentItems = filteredSuppliers.slice(indexOfFirstItem, indexOfLastItem);
   
     // Total pages
     const totalPages = Math.ceil(filteredSuppliers.length / itemsPerPage);
   
     const handlePageChange = (newPage) => {
       if (newPage >= 1 && newPage <= totalPages) {
         setCurrentPage(newPage);
       }
     };

  if (loading) return <LoadingSpinner isVisible={true} />;
  if (error) return <div>Error: {error}</div>;

  return (
    <>
    <div className="header-supplierhistory">
      <h1 className="header-supplierhistory">Supplier Receipt History</h1>
      <button onClick={showInfo} className="info-btn-supplierhistory">
              i
            </button>
    </div>
    <div className="container-supplier-history">
      <div className="top-section-supplier-history">
        <div className="search-container-supplier-history">
          <input
            type="text"
            placeholder="Search Supplier Name or Code..."
            value={search}
            onChange={handleSearchChange}
            className="search-box-supplier-history"
          />
          <input
            type="date"
            placeholder="Date From"
            value={dateFrom}
            onChange={handleDateFromChange}
            className="search-box-supplier-history"
          />
           <h5> to </h5>
          <input
            type="date"
            placeholder="Date To"
            value={dateTo}
            onChange={handleDateToChange}
            className="search-box-supplier-history"
          />
        </div>
      </div>

      <table className="supplier-history-table">
        <thead>
          <tr>
            <th>Supplier Name</th>
            <th>Code</th>
            <th>Date</th>
            <th>Total Amount</th>
            <th>Action</th>
          </tr>
        </thead>
        <tbody>
          {currentItems.map(supplier => (
            <tr key={supplier.id}>
              <td>{supplier.supplierName}</td>
              <td>{supplier.supplierCode}</td>
              <td>{new Date(supplier.date).toLocaleString('en-US', {
                year: 'numeric',
                month: '2-digit',
                day: '2-digit',
                hour: '2-digit',
                minute: '2-digit',
                hour12: true
          })}</td>
              <td>₱{supplier.totalAmount}</td>
              <td>
                <button onClick={() => handleViewClick(supplier.id)} className="view-btn-supplier-history">
                  View
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      {/* Pagination Controls */}
      <div className="pagination-controls">
            <button className="paginationButton"
              onClick={() => handlePageChange(1)} 
              disabled={currentPage === 1}
            >
              <HiChevronDoubleLeft />
            </button>
            <button className="paginationButton"
              onClick={() => handlePageChange(currentPage - 1)} 
              disabled={currentPage === 1}
            >
              <HiChevronLeft />
            </button>
            <span>Page {currentPage} of {totalPages}</span>
            <button className="paginationButton"
              onClick={() => handlePageChange(currentPage + 1)} 
              disabled={currentPage === totalPages}
            >
              <HiChevronRight />
            </button>
            <button className="paginationButton"
              onClick={() => handlePageChange(totalPages)} 
              disabled={currentPage === totalPages}
            >
              <HiChevronDoubleRight />
            </button>
          </div>
    </div>
    </>
  );
};

export default SuppliersHistory;